import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import SkillTagInput from '../components/SkillTagInput';
import type { OnboardingDraft, OnboardingErrors } from '../types';
import { normalizeSkillsArray } from './ProfileResumeFields';

/** Max skills on Work tab / job profile (matches JobPreferencesPanel). */
export const PROFILE_SKILLS_MAX = 20;

export interface ProfileSkillsFieldsProps {
  idPrefix: 'onb' | 'prof' | 'prof-work';
  draft: OnboardingDraft;
  errors: OnboardingErrors;
  onPatch: (patch: Partial<OnboardingDraft>) => void;
}

const ProfileSkillsFields: React.FC<ProfileSkillsFieldsProps> = ({
  idPrefix,
  draft,
  errors,
  onPatch,
}) => {
  const { t } = useTranslation();
  const isWorkTab = idPrefix === 'prof-work';

  // Auto-clean skills that contain "Category: skill" leftovers from resume parsing.
  // Runs once on mount; if the cleaned array differs, patches immediately so the
  // corrected list is persisted on the next Save.
  useEffect(() => {
    if (draft.skills.length === 0) return;
    const cleaned = normalizeSkillsArray(draft.skills);
    const changed =
      cleaned.length !== draft.skills.length ||
      cleaned.some((s, i) => s !== draft.skills[i]);
    if (changed) onPatch({ skills: cleaned });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SkillTagInput
      id={`${idPrefix}-skills`}
      label={t('profile.fields.skills.label', 'Skills')}
      value={draft.skills}
      onChange={(next) => onPatch({ skills: next })}
      placeholder={t('profile.fields.skills.placeholder', 'React, TypeScript, SQL, Figma…')}
      maxTags={PROFILE_SKILLS_MAX}
      error={errors.skills ?? null}
      hideLabel={isWorkTab}
      variant={isWorkTab ? 'profile' : 'default'}
      panelClassName={isWorkTab ? 'ds-work-skills-panel' : undefined}
    />
  );
};

export default ProfileSkillsFields;
